// ─────────────────────────────────────────────────────────
// Einstellungen → Einheiten (Mengeneinheiten + Standardwerte, plancraft-Stil)
// ─────────────────────────────────────────────────────────
// Eigenes Modul. Deutsche UI. Nutzt /api/units (org-scoped, units:read/write).
// Zwei Karten: (1) Einheiten-Liste (Label hinzufügen/löschen), (2) Standardwerte
// (Vorauswahl-Einheit je Positionsart). Nur Verwaltung — Offerten-Nutzung folgt separat.
// Sprache: alle sichtbaren Texte über t() — Keys in i18n/de.json + en.json.
import { icon, escapeHtml } from '../../utils/dom.js';
import { saveUnits, saveUnitDefaults } from '../../core/org.js';
import { t } from '../../i18n/index.js';

// Positionsart-Felder der Standardwerte (nur Keys — Label kommt aus positionKinds.*
// bzw. settings.units.newPosition, siehe defaultFieldLabel()).
const DEFAULT_FIELDS = ['position', 'lohn', 'material', 'geraete', 'fremdleistungen', 'sonstiges'];

function defaultFieldLabel(k) {
  return k === 'position' ? t('settings.units.newPosition') : t('positionKinds.' + k);
}

function unitData(s) {
  return s.unitData || { units: [], defaults: {} };
}

// ── Listen-Bereich (zwei Karten) ──────────────────────────
export function renderUnitsSection(ctx) {
  const { s, can } = ctx;
  if (!can('units:read')) return `<div class="cd-set-empty">${escapeHtml(t('common.noPermission'))}</div>`;
  if (s.unLoading) return `<div class="cd-set-empty">${escapeHtml(t('common.loading'))}</div>`;

  const d = unitData(s);
  const canWrite = can('units:write');

  // Karte 1 — Einheiten
  const unitsGrid = d.units.length
    ? `<div class="cd-un-grid">${d.units.map((u) => `<span class="cd-un-chip">${escapeHtml(u)}</span>`).join('')}</div>`
    : `<div class="cd-emp-empty">${escapeHtml(t('settings.units.empty'))}</div>`;
  const card1 = `
    <div class="cd-emp-card">
      <div class="cd-hd-cardhead">
        <div class="cd-emp-card-head">${icon('straighten', 18)}<h3>${escapeHtml(t('settings.units.title'))}</h3></div>
        ${canWrite ? `<button class="cd-btn-ghost" data-action="un-list-open">${icon('edit', 15)}${escapeHtml(t('common.edit'))}</button>` : ''}
      </div>
      ${unitsGrid}
    </div>`;

  // Karte 2 — Standardwerte
  const defs = d.defaults || {};
  const defRows = DEFAULT_FIELDS.map(
    (k) => `<div class="cd-un-def"><span class="cd-hd-label">${escapeHtml(defaultFieldLabel(k))}</span><span>${escapeHtml(defs[k] || '–')}</span></div>`,
  ).join('');
  const card2 = `
    <div class="cd-emp-card">
      <div class="cd-hd-cardhead">
        <div class="cd-emp-card-head">${icon('tune', 18)}<h3>${escapeHtml(t('settings.units.defaultsTitle'))}</h3></div>
        ${canWrite ? `<button class="cd-btn-ghost" data-action="un-def-open">${icon('edit', 15)}${escapeHtml(t('common.edit'))}</button>` : ''}
      </div>
      <div class="cd-un-defs">${defRows}</div>
      <div class="cd-emp-info cd-un-note">${icon('info', 18)}<span>${escapeHtml(t('settings.units.defaultsNote'))}</span></div>
    </div>`;

  return `<div class="cd-emp">${card1}${card2}</div>`;
}

// ── Modal 1: Einheiten-Liste ──────────────────────────────
export function renderUnitsListModal(ctx) {
  const { s } = ctx;
  const e = s.unListEdit;
  if (!e || !e.open) return '';
  const dis = e.saving ? 'disabled' : '';
  const rows = e.units.length
    ? e.units
        .map(
          (u) => `
        <div class="cd-un-lrow">
          <span>${escapeHtml(u)}</span>
          <button class="cd-emp-rowbtn" data-action="un-del" data-label="${escapeHtml(u)}" title="${escapeHtml(t('common.delete'))}" ${dis}>${icon('delete', 18)}</button>
        </div>`,
        )
        .join('')
    : `<div class="cd-emp-empty">${escapeHtml(t('settings.units.listEmpty'))}</div>`;

  return `
    <div class="cd-emp-modal-overlay" data-action="un-noop">
      <div class="cd-emp-modal cd-un-modal" data-action="un-stop">
        <div class="cd-emp-modal-head">
          <h2>${icon('straighten', 20)}${escapeHtml(t('settings.units.title'))}</h2>
          <div class="cd-set-head-actions">
            <button class="cd-btn-ghost" data-action="un-list-close" ${dis}>${escapeHtml(t('common.cancel'))}</button>
            <button class="cd-btn-primary" data-action="un-list-save" ${dis}>${escapeHtml(e.saving ? t('common.saving') : t('common.save'))}</button>
          </div>
        </div>
        <div class="cd-emp-modal-body">
          <div class="cd-un-add">
            <input class="cd-input" id="un-add-input" type="text" placeholder="${escapeHtml(t('settings.units.addPlaceholder'))}" value="${escapeHtml(e.input || '')}" ${dis} autocomplete="off">
            <button class="cd-un-addbtn" data-action="un-add" title="${escapeHtml(t('common.add'))}" ${dis}>${icon('check', 20)}</button>
          </div>
          ${e.error ? `<div class="cd-un-err">${escapeHtml(e.error)}</div>` : ''}
          <div class="cd-un-list">${rows}</div>
        </div>
      </div>
    </div>`;
}

// ── Modal 2: Standardwerte ────────────────────────────────
export function renderUnitDefaultsModal(ctx) {
  const { s } = ctx;
  const e = s.unDefEdit;
  if (!e || !e.open) return '';
  const dis = e.saving ? 'disabled' : '';
  const units = unitData(s).units || [];
  const opts = (sel) =>
    ['<option value="">–</option>']
      .concat(units.map((u) => `<option value="${escapeHtml(u)}" ${sel === u ? 'selected' : ''}>${escapeHtml(u)}</option>`))
      .join('');
  const select = (k) => `<div class="cd-select-shell"><select class="cd-input" id="un-def-${k}" ${dis}>${opts(e.defaults[k] || '')}</select>${icon('expand_more', 18)}</div>`;

  return `
    <div class="cd-emp-modal-overlay" data-action="un-noop">
      <div class="cd-emp-modal cd-un-modal" data-action="un-stop">
        <div class="cd-emp-modal-head">
          <h2>${icon('tune', 20)}${escapeHtml(t('settings.units.defaultsTitle'))}</h2>
          <div class="cd-set-head-actions">
            <button class="cd-btn-ghost" data-action="un-def-close" ${dis}>${escapeHtml(t('common.cancel'))}</button>
            <button class="cd-btn-primary" data-action="un-def-save" ${dis}>${escapeHtml(e.saving ? t('common.saving') : t('common.save'))}</button>
          </div>
        </div>
        <div class="cd-emp-modal-body">
          ${e.error ? `<div class="cd-error">${icon('error', 18)}<span>${escapeHtml(e.error)}</span></div>` : ''}
          <label class="cd-field"><span>${escapeHtml(t('settings.units.newPosition'))}</span>${select('position')}</label>
          <div class="cd-form-section">${escapeHtml(t('settings.units.calculationSection'))}</div>
          <div class="cd-un-defgrid">
            <label class="cd-field"><span>${escapeHtml(t('positionKinds.lohn'))}</span>${select('lohn')}</label>
            <label class="cd-field"><span>${escapeHtml(t('positionKinds.material'))}</span>${select('material')}</label>
            <label class="cd-field"><span>${escapeHtml(t('positionKinds.geraete'))}</span>${select('geraete')}</label>
            <label class="cd-field"><span>${escapeHtml(t('positionKinds.fremdleistungen'))}</span>${select('fremdleistungen')}</label>
            <label class="cd-field"><span>${escapeHtml(t('positionKinds.sonstiges'))}</span>${select('sonstiges')}</label>
          </div>
        </div>
      </div>
    </div>`;
}

// ── Aktions-Handler ───────────────────────────────────────
export function handleUnitAction(a, el, ctx) {
  const { s, draw, reload } = ctx;
  switch (a) {
    case 'un-noop':
    case 'un-stop':
      return true;

    // ── Einheiten-Liste ──
    case 'un-list-open':
      if (!ctx.can('units:write')) return true;
      s.unListEdit = { open: true, units: [...(unitData(s).units || [])], input: '', error: '', saving: false };
      draw();
      return true;
    case 'un-list-close':
      s.unListEdit = null; draw();
      return true;
    case 'un-add': {
      const e = s.unListEdit;
      if (!e) return true;
      const val = (document.getElementById('un-add-input')?.value || '').trim();
      if (!val) { e.input = ''; return true; }
      if (e.units.some((u) => u.toLowerCase() === val.toLowerCase())) {
        e.input = val; e.error = t('settings.units.duplicateError'); draw(); return true;
      }
      e.units = [...e.units, val].sort((x, y) => x.localeCompare(y, 'de', { sensitivity: 'base' }));
      e.input = ''; e.error = ''; draw();
      return true;
    }
    case 'un-del': {
      const e = s.unListEdit;
      if (!e) return true;
      // getippten Text vor Redraw sichern
      e.input = document.getElementById('un-add-input')?.value ?? e.input;
      e.units = e.units.filter((u) => u !== el.dataset.label);
      e.error = ''; draw();
      return true;
    }
    case 'un-list-save': {
      const e = s.unListEdit;
      if (!e) return true;
      e.saving = true; e.error = ''; draw();
      saveUnits(e.units)
        .then(() => { s.unListEdit = null; if (reload) reload(); else draw(); })
        .catch((err) => { if (s.unListEdit) { s.unListEdit.saving = false; s.unListEdit.error = err.message || t('common.saveFailed'); } draw(); });
      return true;
    }

    // ── Standardwerte ──
    case 'un-def-open':
      if (!ctx.can('units:write')) return true;
      s.unDefEdit = { open: true, defaults: { ...(unitData(s).defaults || {}) }, error: '', saving: false };
      draw();
      return true;
    case 'un-def-close':
      s.unDefEdit = null; draw();
      return true;
    case 'un-def-save': {
      const e = s.unDefEdit;
      if (!e) return true;
      const keys = ['position', 'lohn', 'material', 'geraete', 'fremdleistungen', 'sonstiges'];
      const defaults = {};
      keys.forEach((k) => { defaults[k] = document.getElementById('un-def-' + k)?.value || null; });
      e.saving = true; e.error = ''; draw();
      saveUnitDefaults(defaults)
        .then(() => { s.unDefEdit = null; if (reload) reload(); else draw(); })
        .catch((err) => { if (s.unDefEdit) { s.unDefEdit.saving = false; s.unDefEdit.error = err.message || t('common.saveFailed'); } draw(); });
      return true;
    }
    default:
      return false;
  }
}
